import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { TRANSLATION_SCOPE } from '@modules/course/classroom/i18n/scope';
import { UserProfile } from '@modules/identity/models/user';
import { IdentityStoreService } from '@modules/identity/store/services/identity-store.service';
import { Instructor } from '@modules/instructor/models/instructor';
import { format, isSameDay, parseISO } from 'date-fns';
import { Observable, Subject } from 'rxjs';
import { AvailableSchedule, ClassroomCourse } from '../../models';


@Component({
  selector: 'app-classroom-schedules-modal',
  templateUrl: './classroom-schedules-modal.component.html',
  styleUrls: ['./classroom-schedules-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ClassroomScheduleModalComponent implements OnInit, OnDestroy {

  translationScope = TRANSLATION_SCOPE;

  schedules: any[] = [];

  selectedSchedule = null;
  selectedScheduleId = 0;
  selectedScheduleTimeId = 0;
  dateSlideIndex = 1;
  timeSlideIndex = 1;
  scheduleDateGroups: any[] = [];
  scheduleTimeGroups: any[] = [];

  seats = 0;

  multipleParticipant: FormControl = null;
  numberOfParticipant: FormControl = null;

  classroomCourse: ClassroomCourse;

  userInfo$: Observable<UserProfile>;
  instructor: Instructor;


  private destroy$: Subject<any>;

  constructor(
    public matDialogRef: MatDialogRef<ClassroomScheduleModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private identityStoreService: IdentityStoreService,
    private router: Router,
    private authService: AuthService
  ) {
    this.destroy$ = new Subject();
    this.userInfo$ = this.identityStoreService.getUserProfile();
  }

  ngOnInit() {
    const { schedules, selectedScheduleId, classroom, instructor } = this.data;
    this.classroomCourse = classroom;
    this.instructor = instructor;
    this.schedules = schedules;
    this.selectedScheduleId = selectedScheduleId;
    const selectedSchedule = schedules.find((s) => s.id === selectedScheduleId );

    this.paginateScheduleDate();
    if(selectedScheduleId) {
      this.paginateScheduleTime(selectedSchedule);
    }

    this.multipleParticipant = new FormControl(false);
    this.numberOfParticipant = new FormControl({value: 1, disabled: true}, [Validators.min(1)]);
  }

  ngOnChanges(): void {
    this.paginateScheduleDate();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectedScheduleDate(schedule: AvailableSchedule) {

    this.selectedScheduleId = schedule.id;

    this.selectedSchedule = null;
    this.selectedScheduleTimeId = 0;
    this.seats = 0;
    this.paginateScheduleTime(schedule);
  }

  selectedScheduleTime(schedule: AvailableSchedule) {
    this.selectedSchedule = schedule;
    this.selectedScheduleTimeId = schedule.id;
    this.seats = schedule.bookingLeft;
  }

  previousDateGroup() {
    if (this.dateSlideIndex > 1) this.dateSlideIndex -= 1;
  }

  nextDateGroup() {
    if (this.dateSlideIndex < this.scheduleDateGroups.length) this.dateSlideIndex += 1;
  }

  previousTimeGroup() {
    if (this.timeSlideIndex > 1) this.timeSlideIndex -= 1;
  }

  nextTimeGroup() {
    if (this.timeSlideIndex < this.scheduleTimeGroups.length) this.timeSlideIndex += 1;
  }

  decreaseParticipant(): void {
    const value = Number(this.numberOfParticipant.value)
    if(value > 1) this.numberOfParticipant.setValue(value-1)
  }

  increaseParticipant(): void {
    const value = Number(this.numberOfParticipant.value)
    if(value < this.seats) this.numberOfParticipant.setValue(value+1)
  }

  selectSchedule(): void {
    this.matDialogRef.close({
      schedule: this.selectedSchedule,
      multipleParticipant: this.multipleParticipant.value,
      numberOfParticipant: this.numberOfParticipant.value
    });
  }

  requestBooking(user: any) {
    if (this.selectSchedule) {
      if(this.multipleParticipant.value) {
        this.gotoParticipantInfoPage();
      } else {
        this.redirectToBookingRequest();
      }
      this.matDialogRef.close({ closeWithoutBuying: false });
    }
  }

  paginateScheduleDate() {
    // filter only valid schedule
    let validSchedules = this.schedules.filter((schedule) => schedule.status != false);

    const uniqueSchedules = validSchedules.reduce((acc, current) => {
      const x = acc.find((item) => isSameDay(parseISO(item.startDate), parseISO(current.startDate)));
      if (!x) {
        return acc.concat([current]);
      } else {
        return acc;
      }
    }, []);

    // group schedules by 9 items
    this.scheduleDateGroups = uniqueSchedules.reduce((acc, curr, i) => {
      if (!(i % 9)) {
        acc.push(uniqueSchedules.slice(i, i + 9));
      }
      return acc;
    }, []);
  }

  paginateScheduleTime(schedule) {
    const schedulesTimes = this.schedules.filter((item) =>
      isSameDay(parseISO(item.startDate), parseISO(schedule.startDate))
    );

    this.scheduleTimeGroups = schedulesTimes.reduce((acc, curr, i) => {
      if (!(i % 6)) {
        acc.push(schedulesTimes.slice(i, i + 6));
      }
      return acc;
    }, []);
  }

  private gotoParticipantInfoPage() {
    this.router.navigate([`/kurs/participants/`], {
      queryParams: {
        id: this.classroomCourse.id,
        date: format(parseISO(this.selectedSchedule.startDate), 'yyyy-MM-dd'),
        schedule: this.selectedSchedule.id,
        participant: this.numberOfParticipant.value
      },
      replaceUrl: false
    });
  }

  redirectToBookingRequest() {
    const startDate = format(parseISO(this.selectedSchedule.startDate), 'yyyy-MM-dd');
    this.router.navigateByUrl(
      `/booking/${this.classroomCourse.id}/classroom/request/${startDate}?scheduleId=${this.selectedSchedule.id}`
    );
  }

  loginOrRegister() {
    this.authService.login(this.router.url.toString());
    this.matDialogRef.close();
  }

  closeWithoutBuying() {
    this.matDialogRef.close({ closeWithoutBuying: true });
  }
}
